import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '@/config/firebase';
import { sendEmailVerification } from 'firebase/auth';
import { useToast } from '@/hooks/use-toast';
import { Navbar } from '@/components/layout/Navbar';
import { Loader2 } from 'lucide-react';

const displayFont = { fontFamily: '"Inter", sans-serif' };

export function VerifyEmailPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { toast } = useToast();
    const [isResending, setIsResending] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [email, setEmail] = useState<string>('');

    useEffect(() => {
        // Get email from navigation state or current user
        const stateEmail = location.state?.email;
        const currentUser = auth.currentUser;

        if (stateEmail) {
            setEmail(stateEmail);
        } else if (currentUser?.email) {
            setEmail(currentUser.email);
        }

        // If user is already verified, redirect to dashboard
        if (currentUser?.emailVerified) {
            navigate('/dashboard', { replace: true });
        }
    }, [location.state, navigate]);

    // Cooldown timer
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => {
                setResendCooldown(resendCooldown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    const handleResendVerification = async () => {
        if (resendCooldown > 0 || !auth.currentUser) return;

        try {
            setIsResending(true);

            // Configure action code settings for custom URL
            const actionCodeSettings = {
                url: 'https://uniscout.ai/dashboard',
                handleCodeInApp: true,
            };

            await sendEmailVerification(auth.currentUser, actionCodeSettings);

            toast({
                title: 'Verification email sent!',
                description: 'Please check your inbox and spam folder.',
                variant: 'success',
            });

            // Set 60 second cooldown
            setResendCooldown(60);
        } catch (error: any) {
            toast({
                title: 'Failed to send email',
                description: error.message || 'Please try again later.',
                variant: 'destructive',
            });
        } finally {
            setIsResending(false);
        }
    };

    const handleCheckVerification = async () => {
        if (!auth.currentUser) {
            toast({
                title: 'Not authenticated',
                description: 'Please sign in again.',
                variant: 'destructive',
            });
            navigate('/login', { replace: true });
            return;
        }

        try {
            setIsChecking(true);

            // Reload user to get latest email verification status
            await auth.currentUser.reload();

            if (auth.currentUser.emailVerified) {
                toast({
                    title: 'Email verified!',
                    description: 'Welcome to UniScout!',
                    variant: 'success',
                });
                navigate('/dashboard', { replace: true });
            } else {
                toast({
                    title: 'Email not verified yet',
                    description: 'Please check your inbox and click the verification link.',
                    variant: 'default',
                });
            }
        } catch (error: any) {
            toast({
                title: 'Error checking verification',
                description: error.message || 'Please try again.',
                variant: 'destructive',
            });
        } finally {
            setIsChecking(false);
        }
    };

    return (
        <div className="bg-unicorn-mesh-light dark:bg-none dark:bg-background-dark text-slate-900 dark:text-white overflow-x-hidden antialiased transition-colors duration-300 min-h-screen relative flex flex-col" style={displayFont}>
            <style>{`
        .material-symbols-outlined { font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24; }
        .filled-icon { font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24; }
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .dark ::-webkit-scrollbar-thumb { background: #334155; }
        .dark ::-webkit-scrollbar-thumb:hover { background: #475569; }
        .gradient-text {
            background-clip: text;
            -webkit-background-clip: text;
            color: transparent;
            background-image: linear-gradient(to right, #3B52D0, #4194F2, #A378CF, #F0766E);
        }
        .glass-card {
            background: rgba(255, 255, 255, 0.7);
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
        }
        .dark .glass-card {
            background: rgba(15, 23, 42, 0.6);
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        .animate-pulse-slow {
          animation: pulse-slow 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>

            <div className="fixed inset-0 z-[-1] opacity-0 dark:opacity-40 bg-unicorn-mesh pointer-events-none transition-opacity duration-500"></div>

            <Navbar variant="auth" />

            <main className="flex-grow flex items-center justify-center relative py-12 md:py-20 overflow-hidden">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full -z-10 pointer-events-none opacity-60 dark:opacity-30">
                    <div className="absolute top-[5%] left-[15%] w-[500px] h-[500px] bg-[#3B52D0]/20 rounded-full blur-[100px] animate-pulse-slow"></div>
                    <div className="absolute bottom-[10%] right-[15%] w-[400px] h-[400px] bg-[#F0766E]/20 rounded-full blur-[100px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
                    <div className="absolute top-[40%] right-[30%] w-[300px] h-[300px] bg-[#A378CF]/10 rounded-full blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
                </div>

                <div className="w-full max-w-md px-6 relative z-10">
                    <div className="glass-card rounded-2xl border border-white/50 dark:border-slate-700/50 p-8 shadow-2xl backdrop-blur-xl">
                        <div className="text-center mb-8">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#3B52D0]/10 to-[#F0766E]/10 mb-4">
                                <span className="material-symbols-outlined text-[#4f46e5] text-4xl">mail</span>
                            </div>
                            <h1 className="text-3xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">Verify Your Email</h1>
                            <p className="text-slate-600 dark:text-slate-400 mb-4">
                                We've sent a verification link to
                            </p>
                            <p className="text-[#4f46e5] font-bold text-lg mb-4">{email || 'your email'}</p>
                        </div>

                        <div className="space-y-4 mb-6">
                            <div className="flex items-start gap-3 p-4 rounded-lg bg-indigo-50/50 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-800/30">
                                <span className="material-symbols-outlined text-[#4f46e5] text-xl mt-0.5 shrink-0">info</span>
                                <div className="text-sm text-slate-600 dark:text-slate-400">
                                    <p className="font-semibold mb-1 text-slate-900 dark:text-white">Check your inbox</p>
                                    <p>Click the verification link in the email to activate your account. Don't forget to check your spam folder!</p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <button
                                onClick={handleCheckVerification}
                                disabled={isChecking}
                                className="w-full bg-logo-gradient hover:shadow-lg hover:shadow-purple-500/30 text-white h-12 rounded-lg font-bold transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {isChecking ? (
                                    <>
                                        <Loader2 className="h-5 w-5 animate-spin" />
                                        <span>Checking...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>I've Verified - Continue</span>
                                        <span className="material-symbols-outlined text-sm">arrow_forward</span>
                                    </>
                                )}
                            </button>

                            <button
                                onClick={handleResendVerification}
                                disabled={isResending || resendCooldown > 0}
                                className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-white font-medium h-12 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isResending ? (
                                    <>
                                        <Loader2 className="h-5 w-5 animate-spin" />
                                        <span>Sending...</span>
                                    </>
                                ) : resendCooldown > 0 ? (
                                    <>
                                        <span className="material-symbols-outlined text-sm">schedule</span>
                                        <span>Resend in {resendCooldown}s</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="material-symbols-outlined text-sm">refresh</span>
                                        <span>Resend Verification Email</span>
                                    </>
                                )}
                            </button>
                        </div>

                        <div className="mt-8 text-center">
                            <button
                                onClick={() => navigate('/login')}
                                className="text-sm text-slate-500 dark:text-slate-400 hover:text-[#4f46e5] transition-colors font-medium"
                            >
                                ← Back to Login
                            </button>
                        </div>
                    </div>

                    <div className="mt-8 text-center">
                        <p className="text-xs text-slate-400 dark:text-slate-500">
                            Having trouble? <a className="underline hover:text-slate-500 dark:hover:text-slate-300 ml-1" href="mailto:support@uniscout.ai">Contact Support</a>
                        </p>
                    </div>
                </div>
            </main>

            <footer className="bg-white/50 dark:bg-slate-900 border-t border-indigo-100 dark:border-slate-800 py-8 backdrop-blur-sm mt-auto">
                <div className="max-w-[1280px] mx-auto px-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-4 text-slate-400 dark:text-slate-600">
                        <img alt="uniscout Logo" className="h-6 w-auto opacity-50 grayscale" src="/logo.png" />
                    </div>
                    <p className="text-slate-400 dark:text-slate-600 text-xs">© 2024 uniscout.ai. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
}
